import { GoogleGenAI } from "@google/genai";
import { Product, ChatMessage } from "../types";

// Always use the process.env.API_KEY directly as per guidelines
// We initialize the client inside the functions to ensure we pick up the latest key
// and avoid errors if the key is not yet set at module load time.

export const getCategorySummary = async (category: string, products: Product[]): Promise<string> => {
  const apiKey = process.env.API_KEY;
  if (!apiKey) return "A hand-picked selection of high-quality cleaning tools for a beautiful home.";

  try {
    const ai = new GoogleGenAI({ apiKey });
    const relevantProducts = products.filter(p => category === 'All' || p.category === category);
    const context = relevantProducts.map(p => `- ${p.name}: ${p.description}`).join('\n');

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Summarize the following product range for the "${category}" category:
      ${context}`,
      config: {
        systemInstruction: "You are the Queen Of Clean curator. Provide an enthusiastic and helpful 2-sentence summary of the cleaning range provided. Use engaging, expert language.",
        temperature: 0.7,
      }
    });

    return response.text?.trim() || "A hand-picked selection of high-quality cleaning tools for a beautiful home.";
  } catch (error) {
    console.error("Gemini Summary Error:", error);
    return "Expertly designed solutions for a clean and happy home.";
  }
};

/**
 * Provides expert cleaning advice based on user query, chat history, and the official product range.
 * Uses gemini-3-pro-preview for complex reasoning tasks.
 */
export const getCleaningAdvice = async (
  query: string,
  history: ChatMessage[],
  products: Product[]
): Promise<string> => {
  const apiKey = process.env.API_KEY;
  if (!apiKey) return "I'm sorry, love, I need my sparkle key to help! Please check the API settings.";

  try {
    const ai = new GoogleGenAI({ apiKey });
    const productContext = products.map(p => `- ${p.name}: ${p.description} (Category: ${p.category}, Price: £${p.price})`).join('\n');
    
    const contents = history.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }]
    }));

    contents.push({
      role: 'user',
      parts: [{ text: query }]
    });

    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: contents,
      config: {
        systemInstruction: `You are Lynsey, the "Queen of Clean". You are a world-renowned homecare expert. 
        Your tone is warm, professional, and full of positive energy. You use British English and often use 
        terms of endearment like "love", "darling", or talk about making things "sparkle".
        
        Available Products in your range:
        ${productContext}
        
        Rules:
        1. Recommend specific products from the list above when they are suitable for the user's cleaning task.
        2. Provide expert, practical cleaning hacks and advice.
        3. If you can't solve a problem with the range, give a general high-quality tip.
        4. Keep responses concise (approx 2-4 sentences) and encouraging.`,
        temperature: 0.8,
      }
    });

    return response.text?.trim() || "I'm just refreshing my cleaning tips, darling. Give me one second and try again!";
  } catch (error) {
    console.error("Gemini Advice Error:", error);
    return "I'm just refreshing my cleaning tips, darling. Give me one second and try again!";
  }
};

/**
 * Classifies an Amazon product title into one of the predefined store categories.
 */
export const autoDetectCategory = async (title: string): Promise<string> => {
  const apiKey = process.env.API_KEY;
  if (!apiKey) return "";

  try {
    const ai = new GoogleGenAI({ apiKey });
    const categoriesList = [
      "Lynsey Favorites",
      "Books",
      "Cleaning Tools",
      "Home Interiors",
      "Fashion",
      "Make Up",
      "Home",
      "Storage",
      "Supplements",
      "My Range"
    ];

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Given this product title: "${title}", which of these categories best fits: ${categoriesList.join(", ")}? Reply with only the category name.`,
      config: {
        systemInstruction: "You are a category classification assistant. You must reply with exactly one of the provided category names from the list, and nothing else. No punctuation, no preamble, no markdown. Just the category name.",
        temperature: 0.1,
      }
    });

    const detected = response.text?.trim() || "";
    const matched = categoriesList.find(c => c.toLowerCase() === detected.toLowerCase());
    return matched || "";
  } catch (error) {
    console.error("Gemini Auto-Detect Category Error:", error);
    return "";
  }
};
